import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const STATUS_META = {
  not_paid: { label: 'Not Paid',        color: '#dc2626', dot: '#dc2626', bg: '#FCEBEB' },
  partial:  { label: 'Partially Paid',  color: '#B07F00', dot: '#F5A800', bg: '#FDF4DD' },
  paid_full:{ label: 'Paid in Full',    color: '#15803D', dot: '#15803D', bg: '#E9F7EF' },
};

const InvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const canManage = user?.role === 'boss' || user?.role === 'admin';

  const [payments, setPayments] = useState([]);
  const [status, setStatus] = useState('not_paid');
  const [showPayModal, setShowPayModal] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [newPay, setNewPay] = useState({ amount: '', check_number: '', check_issued: false, check_date: '' });
  const [savingPay, setSavingPay] = useState(false);

  const fetchInvoice = React.useCallback(async () => {
    try {
      const res = await API.get(`/invoices/${id}`);
      setInvoice(res.data);
      setStatus(res.data.payment_status || 'not_paid');
    } catch (err) {
      toast.error('Invoice not found');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchPayments = React.useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await API.get(`/invoices/${id}/payments`);
      setPayments(res.data);
    } catch (err) {
      // silent — panel just shows empty
    }
  }, [id, canManage]);

  useEffect(() => {
    fetchInvoice();
    fetchPayments();
  }, [fetchInvoice, fetchPayments]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this invoice and all its shipments? This cannot be undone.')) return;
    try {
      await API.delete(`/invoices/${id}`);
      toast.success('Invoice deleted');
      navigate('/invoices');
    } catch (err) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleSendEmail = async () => {
    if (!window.confirm(`Email invoice ${invoice.invoice_number} to the customer? This will finalize it.`)) return;
    setSending(true);
    try {
      const res = await API.post(`/invoices/${id}/send`);
      const recipients = (res.data.sent_to || []).join(', ');
      toast.success(`Emailed to ${recipients}`);
      setInvoice(prev => ({ ...prev, ...res.data.invoice }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const handleReopen = async () => {
    if (!window.confirm('Reopen this invoice for editing? You can re-send it afterward.')) return;
    try {
      const res = await API.put(`/invoices/${id}/reopen`);
      setInvoice(prev => ({ ...prev, ...res.data }));
      toast.success('Invoice reopened');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reopen');
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatus(newStatus);
    setSavingStatus(true);
    try {
      await API.put(`/invoices/${id}/payment-status`, { payment_status: newStatus });
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update status');
      setStatus(invoice.payment_status || 'not_paid');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleAddPayment = async () => {
    if (newPay.amount === '' || isNaN(parseFloat(newPay.amount))) {
      return toast.error('Enter a valid amount');
    }
    setSavingPay(true);
    try {
      await API.post(`/invoices/${id}/payments`, {
        amount: parseFloat(newPay.amount),
        check_number: newPay.check_number,
        check_issued: newPay.check_issued,
        check_date: newPay.check_date || null,
      });
      toast.success('Payment recorded');
      setShowPayModal(false);
      setNewPay({ amount: '', check_number: '', check_issued: false, check_date: '' });
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setSavingPay(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await API.delete(`/invoices/${id}/payments/${paymentId}`);
      toast.success('Payment deleted');
      fetchPayments();
    } catch (err) {
      toast.error('Failed to delete payment');
    }
  };

  const thStyle = {
    padding: '10px 8px', background: '#1A2A3A', color: 'white',
    fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
    textAlign: 'left', borderRight: '1px solid #F5A800'
  };

  const tdStyle = {
    padding: '8px', borderBottom: '1px solid #E2E8F0',
    borderRight: '1px solid #f0f0f0', fontSize: '13px'
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  if (!invoice) return null;

  const canDelete = user?.role === 'boss' || user?.role === 'admin';
  const isFinalized = invoice.status === 'finalized';
  const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const balance = parseFloat(invoice.amount_due) - totalPaid;

  return (
    <div style={{ padding: '24px', maxWidth: '100%' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '600' }}>{invoice.invoice_number}</h1>
            {isFinalized ? (
              <span style={{ background: '#E9F7EF', color: '#15803D', border: '1px solid #A7D9BC', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                Finalized
              </span>
            ) : (
              <span style={{ background: '#FDF4DD', color: '#B07F00', border: '1px solid #F2D89A', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                Open
              </span>
            )}
          </div>
          <p style={{ color: '#5B6B7F', fontSize: '14px', marginTop: '4px' }}>
            Customer: <strong>{invoice.customer_name}</strong>
            {isFinalized && invoice.emailed_at ? ` · emailed ${new Date(invoice.emailed_at).toLocaleDateString()}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/invoices')} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
            ← Back
          </button>
          {(canManage || !isFinalized) && (
            <button onClick={() => navigate(`/invoices/${id}/edit`)} style={{ background: '#F5A800', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
              Edit
            </button>
          )}
          <button
            onClick={() => window.open(`/api/invoices/${id}/export/pdf`, '_blank')}
            style={{ background: '#2632dc', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}
          >
            Export PDF
          </button>
          <button
            onClick={() => window.open(`/api/invoices/${id}/export/excel`, '_blank')}
            style={{ background: '#15803D', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}
          >
            Export Excel
          </button>
          {canManage && (
            <button
              onClick={handleSendEmail}
              disabled={sending}
              style={{ background: '#1A2A3A', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: sending ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: sending ? 0.7 : 1 }}
            >
              {sending ? 'Sending…' : (isFinalized ? 'Resend Email' : 'Email to Customer')}
            </button>
          )}
          {canManage && isFinalized && (
            <button onClick={handleReopen} style={{ background: 'none', border: '1px solid #B07F00', color: '#B07F00', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
              Reopen
            </button>
          )}
          {canDelete && (
            <button onClick={handleDelete} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Invoice header details */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Invoice #</p>
            <p style={{ fontWeight: '600' }}>{invoice.invoice_number}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Date</p>
            <p style={{ fontWeight: '600' }}>{new Date(invoice.invoice_date).toLocaleDateString()}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Bill To</p>
            <p style={{ fontWeight: '600' }}>{invoice.bill_to}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Agent #</p>
            <p style={{ fontWeight: '600' }}>{invoice.agent_number}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Amount Due</p>
            <p style={{ fontWeight: '700', fontSize: '18px', color: '#15803D' }}>${parseFloat(invoice.amount_due).toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Payment panel — boss/admin only */}
      {canManage && (
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: '600' }}>Payment Status</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: STATUS_META[status].bg, color: STATUS_META[status].color,
                padding: '4px 12px', borderRadius: '14px', fontSize: '13px', fontWeight: '600'
              }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: STATUS_META[status].dot, display: 'inline-block' }} />
                {STATUS_META[status].label}
              </span>
              <select
                value={status}
                onChange={e => handleStatusChange(e.target.value)}
                disabled={savingStatus}
                style={{ padding: '6px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '13px', outline: 'none', cursor: 'pointer' }}
              >
                <option value="not_paid">Not Paid</option>
                <option value="partial">Partially Paid</option>
                <option value="paid_full">Paid in Full</option>
              </select>
            </div>
          </div>

          {/* Totals strip */}
          <div style={{ display: 'flex', gap: '24px', padding: '12px 16px', background: '#F8FAFC', borderRadius: '6px', marginBottom: '16px' }}>
            <div>
              <p style={{ fontSize: '11px', color: '#5B6B7F' }}>Invoice Total</p>
              <p style={{ fontWeight: '700', fontSize: '15px' }}>${parseFloat(invoice.amount_due).toFixed(2)}</p>
            </div>
            <div>
              <p style={{ fontSize: '11px', color: '#5B6B7F' }}>Total Paid</p>
              <p style={{ fontWeight: '700', fontSize: '15px', color: '#15803D' }}>${totalPaid.toFixed(2)}</p>
            </div>
            <div>
              <p style={{ fontSize: '11px', color: '#5B6B7F' }}>Balance</p>
              <p style={{ fontWeight: '700', fontSize: '15px', color: balance > 0.001 ? '#B07F00' : '#15803D' }}>
                ${balance.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Payments list */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#5B6B7F' }}>Payments ({payments.length})</p>
            <button
              onClick={() => setShowPayModal(true)}
              style={{ background: '#F5A800', color: '#3D2E00', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
            >
              + Add payment
            </button>
          </div>

          {payments.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#8595A6', padding: '12px 0' }}>No payments recorded yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {payments.map(p => (
                <div key={p.payment_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontWeight: '700', fontSize: '14px', color: '#15803D' }}>${parseFloat(p.amount).toFixed(2)}</span>
                    <span style={{ fontSize: '13px', color: '#2B2C2E' }}>
                      {p.check_number ? `Check #${p.check_number}` : 'No check #'}
                    </span>
                    <span style={{
                      fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '10px',
                      background: p.check_issued ? '#E9F7EF' : '#FCEBEB',
                      color: p.check_issued ? '#15803D' : '#dc2626'
                    }}>
                      {p.check_issued ? 'Issued' : 'Not issued'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#5B6B7F' }}>
                      {p.check_date ? new Date(p.check_date).toLocaleDateString() : '—'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeletePayment(p.payment_id)}
                    style={{ background: 'none', border: '1px solid #F5B5B5', color: '#dc2626', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shipments table */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '600' }}>
            Shipments ({invoice.shipments?.length || 0})
          </h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['Pro #', 'Zone', 'Weight', 'Bill. Wt', 'P/D', 'Base', 'FSC', 'Liftgate', 'Res', '2-Man', 'Inside', 'Unpack', 'Misc', 'Special', 'Total', 'Notes'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoice.shipments?.map(s => (
                <tr key={s.shipment_id}>
                  <td style={tdStyle}>{s.pro_number}</td>
                  <td style={tdStyle}>{s.zone}</td>
                  <td style={tdStyle}>{s.total_actual_weight}</td>
                  <td style={tdStyle}>{s.billable_weight}</td>
                  <td style={tdStyle}>{s.pickup_or_delivery}</td>
                  <td style={tdStyle}>${parseFloat(s.base_charge).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.fuel_surcharge).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.liftgate_fee).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.residential_charge).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.two_man_charge).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.inside_charge_amt).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.unpack_charge_amt).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.misc_charges).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.special_charges).toFixed(2)}</td>
                  <td style={{ ...tdStyle, fontWeight: '600', color: '#15803D' }}>
                    ${parseFloat(s.final_total).toFixed(2)}
                  </td>
                  <td style={{ ...tdStyle, minWidth: '160px', whiteSpace: 'normal', color: '#5B6B7F' }}>
                    {[s.misc_notes, s.special_notes].map(x => (x ? String(x).trim() : '')).filter(Boolean).join(' — ') || '—'}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#F4F7FB' }}>
                <td colSpan={14} style={{ ...tdStyle, fontWeight: '600', textAlign: 'right' }}>
                  Total Amount Due:
                </td>
                <td style={{ ...tdStyle, fontWeight: '700', color: '#15803D', fontSize: '15px' }}>
                  ${parseFloat(invoice.amount_due).toFixed(2)}
                </td>
                <td style={tdStyle}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Add payment modal */}
      {showPayModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>Record Payment</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Amount *</label>
                <input
                  type="number" step="0.01"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                  placeholder="0.00"
                  value={newPay.amount}
                  onChange={e => setNewPay(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Check Number</label>
                <input
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                  placeholder="e.g. 1042"
                  value={newPay.check_number}
                  onChange={e => setNewPay(prev => ({ ...prev, check_number: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Check Date</label>
                <input
                  type="date"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                  value={newPay.check_date}
                  onChange={e => setNewPay(prev => ({ ...prev, check_date: e.target.value }))}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newPay.check_issued}
                  onChange={e => setNewPay(prev => ({ ...prev, check_issued: e.target.checked }))}
                />
                Check has been issued
              </label>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowPayModal(false); setNewPay({ amount: '', check_number: '', check_issued: false, check_date: '' }); }}
                style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '9px 18px', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddPayment}
                disabled={savingPay}
                style={{ background: '#F5A800', color: '#3D2E00', border: 'none', borderRadius: '6px', padding: '9px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
              >
                {savingPay ? 'Saving...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceView;
